import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fundraiseService } from '../services/fundraiseService';

// Authenticated-only landing for share-link tokens with permission='collaborate'.
// The PublicPipelineView links here when the visitor clicks Accept Invite, and
// PrivateRoute (the parent in App.tsx) bounces unauthed visitors to login with
// a return URL pointing back here, so by the time this component renders we
// know we have a session.
const ClaimPipelineInvite: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'pending' | 'success' | 'error'>('pending');
  const [error, setError] = useState<string | null>(null);
  const [pipelineName, setPipelineName] = useState<string | null>(null);
  const [ownerLabel, setOwnerLabel] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!token) {
        setStatus('error');
        setError('Missing invite token');
        return;
      }
      try {
        const result = await fundraiseService.claimCollaboratorInvite(token);
        if (cancelled) return;
        setPipelineName(result.pipeline.name);
        setOwnerLabel(result.owner_name || result.owner_email || null);
        setStatus('success');
        // Brief pause so the user sees the success state, then route to /fundraise.
        setTimeout(() => {
          if (!cancelled) navigate('/fundraise', { replace: true });
        }, 1500);
      } catch (err: any) {
        if (cancelled) return;
        const message =
          err?.response?.data?.error || err?.message || 'Failed to accept invite';
        setError(message);
        setStatus('error');
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  return (
    <div className="su-public-pipeline-page">
      <div className="su-public-pipeline-empty">
        {status === 'pending' && <p>Accepting your collaborator invite…</p>}
        {status === 'success' && (
          <>
            <h2 className="su-fundraise-modal-title" style={{ marginBottom: 8 }}>
              You&apos;re in
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.7)' }}>
              {pipelineName ? (
                <>
                  You can now collaborate on <strong>{pipelineName}</strong>
                  {ownerLabel ? <> (shared by {ownerLabel})</> : null}.
                </>
              ) : (
                'You can now collaborate on this pipeline.'
              )}
            </p>
            <p style={{ marginTop: 12, color: 'rgba(255,255,255,0.5)' }}>
              Redirecting to your Fundraise pipelines…
            </p>
          </>
        )}
        {status === 'error' && (
          <>
            <h2 className="su-fundraise-modal-title" style={{ marginBottom: 8 }}>
              Couldn&apos;t accept invite
            </h2>
            <p style={{ color: '#f87171' }}>{error}</p>
            <p style={{ marginTop: 16, color: 'rgba(255,255,255,0.5)' }}>
              Ask the pipeline owner for a fresh link, or{' '}
              <a
                href="/fundraise"
                style={{ color: '#a78bfa', textDecoration: 'underline' }}
              >
                go to your Fundraise dashboard
              </a>
              .
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ClaimPipelineInvite;
